// Mock UPI module (spec §8, §9.2, §15): resolve a UPI ID or mobile,
// UPI PIN set-up and verification. Wrong-PIN lockout shares the MPIN lock
// (spec §18.2 — the mobile behaviour, kept until NPCI confirms otherwise).
import type { UpiResolution } from '@/types/phase2.types';
import { loadDb, saveDb } from '../db';
import { mockNow } from '../clock';
import { fail, pause } from '../util';
import { verifyMobile, verifyUpiId } from './beneficiaries';
import { issueUpiTicket, registerUpiTicket } from './payments';

export const resolveUpiIdOrMobile = async (
  input: { kind: 'UPI_ID'; upiId: string } | { kind: 'MOBILE'; mobile: string },
  qrName?: string
): Promise<UpiResolution> => {
  await pause(400);
  if (input.kind === 'UPI_ID') {
    const result = await verifyUpiId(input.upiId);
    if (result.outcome === 'NOT_FOUND') {
      fail('UPI_NOT_FOUND', 'No one is registered on this UPI ID.');
    }
    return {
      kind: 'UPI_ID',
      upiId: input.upiId.trim().toLowerCase(),
      name: qrName || result.holderName || input.upiId.split('@')[0].toUpperCase(),
      flagged: result.outcome === 'FLAGGED',
    };
  }
  const result = await verifyMobile(input.mobile);
  if (result.outcome === 'NOT_FOUND') {
    fail('MOBILE_NOT_ON_UPI', 'This mobile number is not linked to UPI.');
  }
  return {
    kind: 'MOBILE',
    mobile: input.mobile,
    name: qrName || result.holderName || 'UPI USER',
    flagged: false,
  };
};

export const hasUpiPin = async (): Promise<boolean> => {
  await pause(100);
  return Boolean(loadDb().limits.upiPin);
};

const LOCK_MESSAGE = 'Too many attempts. Your MPIN is locked. Use Forgot MPIN in App Settings.';

export const setUpiPin = async (pin: string): Promise<void> => {
  await pause(400);
  const db = loadDb();
  db.limits.upiPin = pin;
  saveDb(db);
};

/** Verifies the UPI PIN and returns a one-time auth ticket for executePayment. */
export const verifyUpiPin = async (pin: string): Promise<{ authTicket: string }> => {
  await pause(400);
  const db = loadDb();
  const now = mockNow(db);
  if (!db.limits.removeTimeLimits && db.limits.mpinLockedUntil && now < db.limits.mpinLockedUntil) {
    fail('PIN_LOCKED', LOCK_MESSAGE);
  }
  if (pin !== db.limits.upiPin) {
    db.limits.mpinFailures += 1;
    if (db.limits.mpinFailures >= 3) {
      db.limits.mpinLockedUntil = now + 24 * 3_600_000;
      db.limits.mpinFailures = 0;
      saveDb(db);
      fail('PIN_LOCKED', LOCK_MESSAGE);
    }
    saveDb(db);
    fail('PIN_INCORRECT', 'Incorrect UPI PIN.');
  }
  db.limits.mpinFailures = 0;
  saveDb(db);
  const ticket = issueUpiTicket();
  registerUpiTicket(ticket);
  return { authTicket: ticket };
};
